import { action, computed } from 'mobx';
import { Model } from 'core/model/Model';
import { WithOptional } from '@kentik/ui-shared/types/util';
import AssetTagValueMappingModel, { AssetTagValueMappingJson } from './AssetTagValueMappingModel';
import AssetTagValueMappingCollection from './AssetTagValueMappingCollection';

export type AssetTagValueJson = {
  id: string;
  value: string;
};

export type AssetTagValueDeserializeJson = WithOptional<AssetTagValueJson, 'id'> & {
  mappings: AssetTagValueMappingJson[];
};

export default class AssetTagValueModel extends Model<AssetTagValueJson> {
  mappingCollection = new AssetTagValueMappingCollection([], { valueModel: this });

  /**
   * No uppercase bunching, just sort by all lower
   */
  getSortValue(field: keyof AssetTagValueJson) {
    if (field === 'value') {
      return this.get('value')?.toLowerCase();
    }

    return super.getSortValue(field);
  }

  /**
   * Provides data for rendering a list of links that route to auto-populated filters in an asset type's own list view.
   */
  @computed
  get usage() {
    return this.mappingCollection.usage;
  }

  /**
   * Determines the presence of mappings on a value.
   */
  @computed
  get isMapped() {
    return this.mappingCollection.unfilteredSize > 0;
  }

  /**
   * Provides a count of mappings to associate with a value in the value table.
   */
  @computed
  get mappingCount() {
    const { size, unfilteredSize } = this.mappingCollection;

    if (size !== unfilteredSize) {
      return `${size} of ${unfilteredSize}`;
    }

    return size;
  }

  /**
   * Returns a set of asset type/id mappings that should be regarded as disabled
   * These are mappings that already have a value under the given key.
   */
  @computed
  get disabledMappings() {
    const disabled = new Set<string>();

    this.collection?.get().forEach((valueModel) => {
      if (valueModel.id !== this.id) {
        (valueModel as AssetTagValueModel).mappingCollection.get().forEach((mappingModel) => {
          const { asset_type, asset_id } = mappingModel.get();
          disabled.add(`${asset_type}:${asset_id}`);
        });
      }
    });

    return disabled;
  }

  @action
  removeMapping(models: AssetTagValueMappingModel | AssetTagValueMappingModel[]) {
    const mappingModels = Array.isArray(models) ? models : [models];

    mappingModels.forEach((mappingModel) => {
      if (!mappingModel.isNew) {
        // mark persisted models for server-side delete
        // @ts-expect-error removedIds is there on the value collection
        this.collection.removedIds.add(mappingModel.id as string);
      }

      this.mappingCollection.remove(mappingModel.id as string);
    });
  }

  /**
   * Provides a mapping delete bulk action
   */
  @computed
  get bulkActions() {
    return [
      {
        name: 'delete',
        label: 'Delete',
        intent: 'danger',
        icon: 'trash',
        handler: action(() => {
          const { mappingCollection } = this;

          mappingCollection.selectedArray.forEach((model) => this.removeMapping(model));
          mappingCollection.clearSelection();
          mappingCollection.resetAssetCollections();
        })
      }
    ];
  }

  deserialize(data: AssetTagValueDeserializeJson) {
    const { mappings = [], ...modelProperties } = data;
    this.mappingCollection.set(mappings);
    return super.deserialize(modelProperties);
  }
}
